import React, { ReactNode, useContext, useState } from "react";
import { musicService } from "../../../api/musicService";
import { IFile } from "../../../common/commonTypes";
import { ICreateMusicDto } from "../../../common/dtos";
import { useSession } from "../../../queries/authentication.query";

const setFile = (file?: IFile) => {
  if (file) {
    return;
  }
  return;
};

interface UploadMusicContextValue {
  master?: IFile;
  setMaster: typeof setFile;
  full?: IFile;
  setFull: typeof setFile;
  instrumental?: IFile;
  setInstrumental: typeof setFile;
  vocal?: IFile;
  setVocal: typeof setFile;
  cover?: IFile;
  setCover: typeof setFile;
  poster?: IFile;
  setPoster: typeof setFile;
  video?: IFile;
  setVideo: typeof setFile;
}

const UploadMusicContext = React.createContext<UploadMusicContextValue>({
  setMaster: setFile,
  setFull: setFile,
  setCover: setFile,
  setPoster: setFile,
  setInstrumental: setFile,
  setVocal: setFile,
  setVideo: setFile,
});
interface UploadMusicProviderProps {
  children?: ReactNode;
}

export const UploadMusicProvider = ({ children }: UploadMusicProviderProps) => {
  const [master, setMaster] = useState<IFile>();
  const [full, setFull] = useState<IFile>();
  const [instrumental, setInstrumental] = useState<IFile>();
  const [vocal, setVocal] = useState<IFile>();
  const [cover, setCover] = useState<IFile>();
  const [poster, setPoster] = useState<IFile>();
  const [video, setVideo] = useState<IFile>();

  return (
    <UploadMusicContext.Provider
      value={{
        master,
        setMaster,
        full,
        setFull,
        instrumental,
        setInstrumental,
        vocal,
        setVocal,
        cover,
        setCover,
        poster,
        setPoster,
        video,
        setVideo,
      }}
    >
      {children}
    </UploadMusicContext.Provider>
  );
};

export function useUploadMusicContext() {
  const musicContext = useContext(UploadMusicContext);

  if (musicContext === undefined) {
    throw new Error(
      "To use useUploadMusicContext you must wrap this component in a <UploadMusicProvider>"
    );
  }

  return musicContext;
}

export function useUploadMusic() {
  const { user } = useSession();
  const musicContext = useUploadMusicContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const uploadTrack = async (
    music: Partial<ICreateMusicDto>,
    onSuccess?: () => void
  ) => {
    try {
      setIsLoading(true);
      setError("");

      const payload: ICreateMusicDto = {
        artist_id: user!.id,
        title: music.title!,
        type: music.type!,
        countries: music.countries || [],
        genres: music.genres || [],
        languages: music.languages || [],
        master_id: musicContext.master!.id,
        master_length: music.master_length!,
        full_id: musicContext.full!.id,
        full_length: music.full_length!,
        cover_id: musicContext.cover!.id,
        poster_id: musicContext.poster?.id,
        backing_id: musicContext.instrumental?.id,
        backing_length: music.backing_length,
        vocal_id: musicContext.vocal?.id,
        vocal_length: music.vocal_length,
        video_id: musicContext.video?.id,
        description: music.description,
        hidden: music.hidden,
      };
      const { data } = await musicService.createMusic(payload);
      onSuccess?.();
      return data.id;
    } catch (e) {
      setError(e.errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const closeError = () => {
    setError("");
  };

  return {
    uploadTrack,
    isLoading,
    error,
    closeError,
  };
}
